import React, { useState, useEffect } from 'react';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { seoConfig } from '../config/seoConfig';
import { MapPin, Users, Mail, Phone, Globe, Info, Calendar, Award } from 'lucide-react';

const ChaptersPage = () => {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchChapters();
  }, []);

  const fetchChapters = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE}/chapters/public`);
      const data = await response.json();

      if (data.success) {
        setChapters(data.data || []);
      } else {
        setError('Failed to load chapters');
      }
    } catch (err) {
      console.error('Error fetching chapters:', err);
      setError('Failed to load chapters');
    } finally {
      setLoading(false);
    }
  };

  const getImageSrc = (logo) => {
    if (!logo) return null;
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    if (logo.startsWith('/uploads/')) {
      return `${BACKEND_URL}${logo}?v=${Date.now()}`;
    }
    return logo;
  };

  return (
    <PublicLayout>
      <SEO
        title={seoConfig.chapters.title}
        description={seoConfig.chapters.description}
        keywords={seoConfig.chapters.keywords}
        url={seoConfig.chapters.url}
      />

      {/* Add animations CSS */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { transform: translateY(-30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeInUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      {/* Header Section with ARCA Colors */}
      <div className="py-4" style={{
        background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)',
        animation: 'fadeIn 0.8s ease-in'
      }}>
        <div className="container text-center text-white">
          <h1 className="h2 fw-bold mb-2" style={{ animation: 'slideDown 0.8s ease-out' }}>
            ARCA Chapters
          </h1>
          <p className="mb-0" style={{ animation: 'slideUp 0.8s ease-out', maxWidth: '800px', margin: '0 auto', fontSize: '0.95rem' }}>
            National respiratory care associations united under ARCA across Africa
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-4" style={{ backgroundColor: '#f8f9fa' }}>
        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: '#6B8E23', width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading chapters...</span>
            </div>
            <p className="mt-3 text-muted">Loading chapters...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Chapters Grid */}
        {!loading && !error && (
          <>
            {chapters.length === 0 ? (
              <div className="text-center py-5">
                <Globe size={64} style={{ color: '#6B8E23', opacity: 0.3 }} />
                <h3 className="mt-3 text-muted">No chapters found</h3>
                <p className="text-muted">Check back soon for updates on ARCA chapters.</p>
              </div>
            ) : (
              <div className="row g-3">
                {chapters.map((chapter, index) => {
                  const logoSrc = getImageSrc(chapter.chapter_logo);
                  return (
                    <div
                      key={chapter.id}
                      className="col-12 col-sm-6 col-md-4 col-lg-3"
                      style={{
                        animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                      }}
                    >
                      <div
                        className="card h-100 border-0 shadow-sm"
                        style={{
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-5px)';
                          e.currentTarget.style.boxShadow = '0 10px 20px rgba(107, 142, 35, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '';
                        }}
                        onClick={() => {
                          setSelectedChapter(chapter);
                          setShowModal(true);
                        }}
                      >
                        {/* ARCA Green Header */}
                        <div className="p-3 text-center text-white" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)' }}>
                          <h6 className="fw-bold mb-1" style={{ fontSize: '0.9rem', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {chapter.chapter_name}
                          </h6>
                          <div className="d-flex align-items-center justify-content-center" style={{ fontSize: '0.8rem' }}>
                            <MapPin size={14} className="me-1" />
                            <span>{chapter.country}</span>
                          </div>
                        </div>

                        {/* Logo Section */}
                        <div className="p-3 bg-light text-center" style={{ minHeight: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {logoSrc ? (
                            <img
                              src={logoSrc}
                              alt={chapter.chapter_name}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '120px',
                                objectFit: 'contain'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center"
                              style={{
                                width: '80px',
                                height: '80px',
                                backgroundColor: '#6B8E23',
                                borderRadius: '12px'
                              }}
                            >
                              <Globe size={40} color="white" />
                            </div>
                          )}
                        </div>

                        {/* View Details Button */}
                        <div className="card-footer bg-transparent border-top p-2">
                          <button
                            className="btn btn-sm w-100 fw-bold text-white"
                            style={{
                              backgroundColor: '#6B8E23',
                              border: 'none',
                              fontSize: '0.85rem'
                            }}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {showModal && selectedChapter && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setShowModal(false)}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ animation: 'slideDown 0.3s ease-out' }}>
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title fw-bold">{selectedChapter.chapter_name}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                {/* Contact Information ONLY */}
                <h6 className="fw-bold mb-3" style={{ color: '#6B8E23' }}>Contact Information</h6>

                {selectedChapter.leader_email && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Email</small>
                    <div className="d-flex align-items-center">
                      <Mail size={20} style={{ color: '#6B8E23' }} className="me-3" />
                      <a href={`mailto:${selectedChapter.leader_email}`} style={{ color: '#6B8E23', textDecoration: 'none', fontWeight: '500' }}>
                        {selectedChapter.leader_email}
                      </a>
                    </div>
                  </div>
                )}

                {selectedChapter.leader_phone && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Phone</small>
                    <div className="d-flex align-items-center">
                      <Phone size={20} style={{ color: '#6B8E23' }} className="me-3" />
                      <a href={`tel:${selectedChapter.leader_phone}`} style={{ color: '#6B8E23', textDecoration: 'none', fontWeight: '500' }}>
                        {selectedChapter.leader_phone}
                      </a>
                    </div>
                  </div>
                )}

                {selectedChapter.website_url && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Website</small>
                    <div className="d-flex align-items-center">
                      <Globe size={20} style={{ color: '#6B8E23' }} className="me-3" />
                      <a href={selectedChapter.website_url} target="_blank" rel="noopener noreferrer" style={{ color: '#6B8E23', textDecoration: 'none', fontWeight: '500' }}>
                        Visit Website
                      </a>
                    </div>
                  </div>
                )}

                {!selectedChapter.leader_email && !selectedChapter.leader_phone && !selectedChapter.website_url && (
                  <p className="text-muted text-center">No contact information available</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn"
                  style={{ backgroundColor: '#6B8E23', color: 'white' }}
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  );
};

export default ChaptersPage;
